import React, { useState, useMemo, useEffect } from "react";
import { useRef } from "react";

import useSelection from '../../../hooks/useSelection';
import { useGeographicUnits } from '../../../hooks/useGeographicUnits';
import IndicatorChart from '../shared/IndicatorChart';
import './RealEstateIndicatorsPage.css';
import jsPDF from "jspdf";
import templateBg from "../../../assets/images/real_estate_indicators_template.png";

import portadaReporte from "../../../assets/images/portada_reporte_invescasa.png";
import pagina1Invescasa from "../../../assets/images/que_es_invescasa_pagina_1.png";
import pagina2Invescasa from "../../../assets/images/que_es_invescasa_pagina_2.png";

import { useAuth } from '../../../context/AuthContext';

import useIndicatorYearRanges from "../../../hooks/useIndicatorYearRanges";

/* === Formatters === */
const formatters = {
  moneyMillions: (v) => (v == null ? '' : `$${(Number(v) / 1_000_000).toFixed(1)}M`),
  money: (v) => (v == null ? '' : `$${Number(v).toLocaleString()}`),
  thousandsK: (v) => (v == null ? '' : `${Math.round(Number(v) / 1000)}k`),
  percent0: (v) => (v == null ? '' : `${Math.round(Number(v))}%`),
  percent1: (v) => (v == null ? '' : `${Number(v).toFixed(1)}%`),
  money1d: (v) => `$${Number(v).toLocaleString(undefined, { 
    minimumFractionDigits: 1, 
    maximumFractionDigits: 1 
  })}`,
  percent1d: (v) => `${Number(v).toFixed(1)}%`,
  thousands1d: (v) => `${(Number(v) / 1000).toFixed(1)}k`,
};

const indicators = [
  { 
    name: "Median House Price ($)", 
    key: "median_house_price", 
    description: "Median house price over time by state; shows how property values are trending.",
    yTickFormatter: formatters.moneyMillions, 
    tooltipFormatter: formatters.money 
  },
  { 
    name: "Median Weekly Rent ($/week)", 
    key: "median_weekly_rent", 
    description: "Median weekly rent by state; compares rental cost and its evolution.",
    yTickFormatter: formatters.money, 
    tooltipFormatter: formatters.money1d 
  },
  { 
    name: "Dwelling Units Commenced", 
    key: "dwelling_units_commenced", 
    description: "New housing constructions started; signals future supply.",
    yTickFormatter: formatters.thousandsK, 
    tooltipFormatter: formatters.money 
  },
  { 
    name: "Total Dwelling Approvals (Units)", 
    key: "dwelling_units_total", 
    description: "Approved residential projects; indicates planned pipeline.",
    yTickFormatter: formatters.thousandsK, 
    tooltipFormatter: formatters.money, 
    useSumApi: true 
  },
  { 
    name: "Vacancy rate (%) per year", 
    key: "vacancy_rate_pct", 
    description: "Percentage of unoccupied rental properties; reflects tightness or slack in rental markets.",
    yTickFormatter: formatters.percent0, 
    tooltipFormatter: formatters.percent1 
  },
  { 
    name: "Rental Yield (%)", 
    key: "rental_yield_pct", 
    description: "Annual rent as a percentage of property value; a quick read of cash-flow return.",
    yTickFormatter: formatters.percent0, 
    tooltipFormatter: formatters.percent1 
  },
]; 

export default function RealEstateIndicatorsPage() {
  const { places, loading } = useGeographicUnits("State");
  const { membership } = useAuth();
  const [multiMode, setMultiMode] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const chartRefs = useRef({}); 
  const indicatorKeys = useMemo(() => indicators.map(i => i.key), []);
  const { yearRanges, loading: yearsLoading } = useIndicatorYearRanges(indicatorKeys);
  

  const getChartImageByKey = async (indicatorKey) => {
  const wrapper = chartRefs.current[indicatorKey];
  if (!wrapper) {
    console.warn("No se encontró el contenedor para", indicatorKey);
    return null;
  }

  const svg = wrapper.querySelector("svg.recharts-surface");
  if (!svg) {
    console.warn("No se encontró el SVG de Recharts para", indicatorKey);
    return null;
  }

  const serializer = new XMLSerializer();
  const svgString = serializer.serializeToString(svg);

  const svgBlob = new Blob([svgString], {
    type: "image/svg+xml;charset=utf-8",
  });
  const url = URL.createObjectURL(svgBlob);

  const imgData = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const rect = svg.getBoundingClientRect();
      const scale = 2;
      const width = rect.width * scale;
      const height = rect.height * scale;

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("No se pudo obtener el contexto del canvas"));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      const dataUrl = canvas.toDataURL("image/png");
      URL.revokeObjectURL(url);
      resolve(dataUrl);
    };

    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };

    img.src = url;
  });

  return imgData; // "data:image/png;base64,..."
};

  

  useEffect(() => {
    const status = membership?.status || '';
    const active = String(status).toLowerCase() === 'active';
    setMultiMode(active);
  }, [membership]);

  const { selected, selectedPlaces, handleClick, setSelected } = useSelection({ 
    multi: multiMode, 
    defaultValue: places && places.length > 0 ? places[0] : 'All', 
    places 
  });

  // Reset selection when multiMode changes
  useEffect(() => {
    if (places && places.length > 0) {
      setSelected(multiMode ? ['All'] : places[0]);
    }
  }, [multiMode, places, setSelected]);

  const sortedPlaces = useMemo(() => (places || []).slice().sort(), [places]);
  const items = loading ? ['All'] : ['All', ...sortedPlaces];

  // Removed duplicate handleClick declaration; using handleClick from useSelection hook.


const handleDownloadPDF = async () => {
  try{
  setIsGenerating(true);   // <--- activar loader

  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "px",
    format: [1920, 1080]
  });
  await new Promise(r => setTimeout(r, 1500));
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
   


  // ============================
  // LOAD TEMPLATE BACKGROUND
  // ============================
  const templateImg = new Image();
  templateImg.src = templateBg;
  templateImg.crossOrigin = "anonymous";
  await new Promise((resolve) => templateImg.onload = resolve);

  // ============================
  // LOAD CARDS & IMAGES
  // ============================

  const portada1 = new Image();
  portada1.src = portadaReporte;
  portada1.crossOrigin = "anonymous";

  const portada2 = new Image();
  portada2.src = pagina1Invescasa;

  const portada3 = new Image();
  portada3.src = pagina2Invescasa;

  await Promise.all([
    new Promise(r => portada1.onload = r),
    new Promise(r => portada2.onload = r),
    new Promise(r => portada3.onload = r),
  ]);

  // ============================
  // PORTADAS
  // ============================
  pdf.addImage(portada1, "PNG", 0, 0, pageWidth, pageHeight);
  pdf.setFont("helvetica", "bold");

  pdf.setFontSize(45);       
  pdf.setTextColor("#ffffff");
  pdf.text("Real Estate Indicators", 380, 685);   

  pdf.addPage();

  pdf.addImage(portada2, "PNG", 0, 0, pageWidth, pageHeight);
  pdf.addPage();

  pdf.addImage(portada3, "PNG", 0, 0, pageWidth, pageHeight);


  pdf.addPage();

  // ============================
  //  TABLE OF CONTENTS 
  // ============================
  //  Background
  pdf.addImage(templateImg, "PNG", 0, 0, pageWidth, pageHeight);

  //  BIG TITLE
  pdf.setFont("helvetica", "bold");

  pdf.setFontSize(125);     
  pdf.setTextColor("#FFFFFF");
  pdf.text("Table of Contents", 450, 145);

  //  LIST FORMAT
  pdf.setFontSize(38);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor("#0f385d"); 

  //  POSICIONES
  const startX = 450;
  const baseY  = 360;
  const stepY  = 65; 

  indicators.forEach((indicator, index) => {
    const itemY = baseY + index * stepY;
    const label = `${index + 1}. ${indicator.name}`;

    pdf.text(label, startX, itemY);
  });
  
  // === BARRA VERTICAL A LA DERECHA ===

  // Posición X de la barra (ajústala si la quieres más afuera)
  const barX = 1550;     

  // Grosor de la barra (más grande = barra más gruesa)
  const barWidth = 11;   

  // Cálculo de altura dinámica según # de indicadores
  const firstItemY = baseY - 38;     // un poquito por encima del primer texto
  const lastItemY  = baseY + (indicators.length - 1) * stepY + 15;

  // Color corporativo
  pdf.setFillColor("#0f385d"); // azul oscuro, cámbialo si quieres

  // Dibujar rectángulo
  pdf.rect(barX, firstItemY, barWidth, lastItemY - firstItemY, "F");



  // ============================
  //  GRAPH PAGES
  // ============================
  let pageCounter = 1;

for (let i = 0; i < indicators.length; i++) {
  const indicator = indicators[i];



  // 1) Obtener la imagen de la gráfica para este indicador
  const imgData = await getChartImageByKey(indicator.key);
  if (!imgData) {
    console.warn("No se pudo generar la imagen para", indicator.key);
    continue; // pasamos al siguiente
  }

  // 2) Nueva página + fondo
  pdf.addPage();
  pdf.addImage(templateImg, "PNG", 0, 0, pageWidth, pageHeight);

    //  Dibujar el título en el PDF
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(32);
  pdf.setTextColor("#000000");
  pdf.text(indicator.name, 200, 300); // ajusta las coordenadas a tu layout


  
  // === LEYENDA DE STATES (varios, en horizontal) ===

  // Puedes reusar marginX y legendY si quieres:
  const legendStartX = 250;   // posición inicial X
  const legendY = 260;        // posición fija Y
  const spacing = 190;        // espacio entre items
  const dotRadius = 5;        // tamaño del punto (radio)


  const legendStates = Array.isArray(selectedPlaces) && selectedPlaces.length
    ? selectedPlaces
    : [];  // si no hay nada seleccionado, queda vacío

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(23);

  legendStates.slice().reverse().forEach((place, index) => {
    const color = colorMap[place] || "#000000";
    const x = legendStartX + index * spacing;

    // 1. Puntito de color
    pdf.setFillColor(color);
    pdf.circle(x - 10, legendY - 5, dotRadius, "F");

    // 2. Texto del estado
    pdf.setTextColor(color);
    pdf.text(place, x, legendY);
  });

  // volver a un color neutro para el texto que viene después
  pdf.setTextColor("#FFFFFF");


  // 3) Añadir la imagen de la gráfica
  const imgWidth =1750;
  const imgHeight = 600;
  const imageY = 350;
  const marginX = 110;


  pdf.addImage(imgData, "PNG", marginX, imageY, imgWidth, imgHeight);

  // Texto AUD Australina Dolar
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(28);
  pdf.setTextColor("#5f5f5f");
  pdf.text("$ AUD", 210,350 );

  // Encabezados
  pdf.setFontSize(48);
  pdf.setTextColor("#FFFFFF");
  pdf.setFont("helvetica", "bold");
  pdf.text("Real estate indicators", 435, 90);

  // Subtítulo por indicador
  pdf.setFontSize(32);
  pdf.text(
    indicator.description,
    435,
    130
  );

  // Número grande
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(130);
  pdf.setTextColor("#000000");
  pdf.text(`${pageCounter}`, 54, 302);

  pageCounter++;
}


  // ============================
  // SAVE PDF
  // ============================
  pdf.save("real-estate-indicators.pdf");

}catch (err) {
    console.error("Error generating PDF:", err);
  } finally {
    setIsGenerating(false);   // <--- desactivar loader
  }
};

  // selectedPlaces already provided by useSelection hook (resolves 'All' to full list).

  const palette = useMemo(() => ([
    '#DFA135', // chart-1
    '#E12020', // chart-2
    '#00A4E4', // chart-3
    '#34BF49', // chart-4
    '#04519E', // chart-5
    '#F47721', // chart-6
    '#D20962', // chart-7
    '#7D3F98', // chart-8
  ]), []);

  const colorMap = useMemo(() => {
    const map = {};
    (places || []).forEach((place, idx) => { map[place] = palette[idx % palette.length]; });
    return map;
  }, [places, palette]);

  /* ===== Modal state ===== */
  const [showModal, setShowModal] = useState(false);
  const [modalIndicator, setModalIndicator] = useState(null);

  const modalYears = useMemo(() => {
    if (!modalIndicator || yearsLoading) return [];
    const range = yearRanges[modalIndicator.key] || [2000, new Date().getFullYear()];
    return Array.from({ length: range[1] - range[0] + 1 }, (_, i) => range[0] + i);
  }, [modalIndicator, yearRanges, yearsLoading]);

  const openModal = (ind) => {
    setModalIndicator(ind);
    setShowModal(true);
    document.body.classList.add('mc-modal-open');
  };
  const closeModal = () => {
    setShowModal(false);
    setModalIndicator(null);
    document.body.classList.remove('mc-modal-open');
  };
  const onOverlayClick = (e) => { if (e.target === e.currentTarget) closeModal(); };
  useEffect(() => {
    const onEsc = (e) => e.key === 'Escape' && closeModal();
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, []);

  return (
    
    <div className="background">
      
      <div className="panel-modal-container">

        {/* BARRA DE TÍTULO */}
        <div className="main-title">
          <h2 className="main-title__title"><strong>Real estate indicators</strong></h2>

          <button 
            className="download-report" 
            aria-label="Download report"
            onClick={handleDownloadPDF}
            disabled={!multiMode || isGenerating || yearsLoading}
            style={!multiMode || isGenerating || yearsLoading ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
          >
            <span className="icon" aria-hidden>
              {isGenerating ? (
                <span className="spinner"></span>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" 
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              )}
            </span>

            {isGenerating ? "Generating..." : "Download report"}
          </button>
        </div>

        {/* BANNER DE ESTADOS */}
        <div className="states-bar">

        <div className="mini-banner">
          
          <span className="banner-label">States</span>
          <div className="tabs">
            {items.map((item) => {
              const isActive = Array.isArray(selected) ? selected.includes(item) : selected === item;
              const isAll = item === 'All';
              const isMultiMode = Array.isArray(selected);
              const color = !isAll ? colorMap[item] : '#092463';
              return (
                <button
                  key={item}
                  className={`tab-button${isActive ? ' active' : ''}${isAll && !isMultiMode ? ' disabled' : ''}`}
                  onClick={() => (isAll && !isMultiMode) ? null : handleClick(item)}
                  aria-pressed={isActive}
                  disabled={isAll && !isMultiMode}
                  style={ isActive && !isAll ? { borderColor: color, background: 'var(--color-blue-040)' } : undefined }
                >
                  <span
                    className={`tab-radio${isActive ? ' active' : ''}`}
                    style={ !isAll ? { borderColor: isActive ? color : 'var(--color-navy)', color: isActive ? color : undefined } : undefined }
                    aria-hidden
                  />
                  <span className="tab-label">{item}</span>
                </button>
              );
            })}
          </div>
           </div>
        </div>

        {/* GRID DE INDICADORES */}
        <div className="indicator-grid">
          {yearsLoading ? (
            <div style={{ padding: 20 }}>Loading years...</div>
          ) : (
            indicators.map((ind) => {
              const range = yearRanges[ind.key] || [2000, new Date().getFullYear()];
              const yearsArray = Array.from(
                { length: range[1] - range[0] + 1 },
                (_, i) => range[0] + i
              );

              return (
                <div className="indicator-card" key={ind.key}>
                  <div className="chart-title">
                    {ind.name}
                    <button
                      className="info-trigger"
                      type="button"
                      aria-label="Expand chart"
                      onClick={() => openModal(ind)}
                    >
                      <svg
                        className="info-icon"
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="4 9 4 4 9 4" />
                        <line x1="4" y1="4" x2="10" y2="10" />

                        <polyline points="15 4 20 4 20 9" />
                        <line x1="20" y1="4" x2="14" y2="10" />

                        <polyline points="20 15 20 20 15 20" />
                        <line x1="20" y1="20" x2="14" y2="14" />

                        <polyline points="9 20 4 20 4 15" />
                        <line x1="4" y1="20" x2="10" y2="14" />
                      </svg>
                    </button>
                  </div>

                  <div
                    className="card-body"
                    ref={(el) => {
                      if (el) chartRefs.current[ind.key] = el;
                      else delete chartRefs.current[ind.key];
                    }}
                  >
                    <IndicatorChart
                      indicatorName={ind.key}
                      places={places || []}
                      selectedPlaces={selectedPlaces}
                      years={yearsArray}
                      yTickFormatter={ind.yTickFormatter}
                      tooltipFormatter={ind.tooltipFormatter}
                      showHeader={false}
                      wrap={false}
                      height={320}
                      colors={palette}
                      useSumApi={!!ind.useSumApi}
                    />
                  </div>
                </div>
              );
            })
          )}

        </div>

      </div>

      {/* ===== MODAL AMPLIADO ===== */}
      {showModal && modalIndicator && (
        <div className="mc-modal__overlay" onClick={onOverlayClick} role="dialog" aria-modal="true" aria-label="Expanded chart">
          <div className="mc-modal">
            <div className="mc-modal__header">
              <div className="mc-modal__title">
                <div className="mc-modal__title-main">{modalIndicator.name}</div>
                <div className="mc-modal__title-sub">
                  {modalIndicator.description}
                </div>
              </div>
              <button className="mc-modal__close" onClick={closeModal} aria-label="Close">✕</button>
            </div>
            <div className="mc-modal__body">
              <div style={{ width: '100%', height: 520 }}>
                <IndicatorChart
                  indicatorName={modalIndicator.key}
                  places={places || []}
                  selectedPlaces={selectedPlaces}
                  years={modalYears}
                  yTickFormatter={modalIndicator.yTickFormatter}
                  tooltipFormatter={modalIndicator.tooltipFormatter}
                  showHeader={false}
                  wrap={false}
                  height={520}
                  colors={palette}
                  showLegend={true}
                  useSumApi={!!modalIndicator.useSumApi}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
